import React, { useState, useEffect, useCallback } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
} from "reactstrap";
import { FormattedMessage, useIntl } from "react-intl";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { dismissProductGuide } from "../../../hooks/productGuideStorage";

export type GuideStepConfig = {
  icon: IconDefinition;
  titleId: string;
  bodyId: string;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  /** Clave i18n del título del modal (ej. app.socialPost.guide.modalTitle) */
  modalTitleId: string;
  steps: GuideStepConfig[];
  /**
   * Mismo id que en `?guide=` (ej. "ai", "social"). Si se define, se muestra
   * «No volver a mostrar» y se guarda en localStorage al cerrar con la casilla marcada.
   */
  persistDismissKey?: string;
};

/**
 * Guía rápida en pasos (mismo patrón que IA): se abre con ?guide=… desde Inicio.
 */
export function ProductGuideModal({
  isOpen,
  onClose,
  modalTitleId,
  steps,
  persistDismissKey,
}: Props) {
  const intl = useIntl();
  const [step, setStep] = useState(0);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const stepCount = steps.length;
  const safeStep = Math.min(step, Math.max(0, stepCount - 1));
  const meta = steps[safeStep];

  useEffect(() => {
    if (isOpen) {
      setStep(0);
      setDontShowAgain(false);
    }
  }, [isOpen]);

  const handleClose = useCallback(() => {
    if (dontShowAgain && persistDismissKey) {
      dismissProductGuide(persistDismissKey);
    }
    onClose();
  }, [dontShowAgain, persistDismissKey, onClose]);

  if (!stepCount || !meta) return null;

  const goNext = () => {
    if (safeStep < stepCount - 1) setStep((s) => s + 1);
    else handleClose();
  };

  const goPrev = () => setStep((s) => Math.max(0, s - 1));

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleClose}
      centered
      className="product-guide-modal"
    >
      <ModalHeader toggle={handleClose}>
        <span className="d-flex align-items-center">
          <FontAwesomeIcon icon={meta.icon} className="text-primary mr-2" />
          <FormattedMessage id={modalTitleId} />
        </span>
      </ModalHeader>
      <ModalBody>
        <div className="product-guide-modal__progress mb-3">
          {Array.from({ length: stepCount }, (_, i) => (
            <span
              key={i}
              className={`product-guide-modal__dot ${
                i === safeStep ? "is-active" : ""
              } ${i < safeStep ? "is-done" : ""}`}
            />
          ))}
        </div>
        <h3 className="h5 font-weight-bold mb-3">
          <FormattedMessage id={meta.titleId} />
        </h3>
        <p className="text-muted mb-0" style={{ lineHeight: 1.6 }}>
          <FormattedMessage id={meta.bodyId} />
        </p>
      </ModalBody>
      <ModalFooter className="product-guide-modal__footer flex-column align-items-stretch">
        {persistDismissKey && (
          <div className="product-guide-modal__no-again form-check mb-3">
            <input
              id="product-guide-no-again"
              className="form-check-input"
              type="checkbox"
              checked={dontShowAgain}
              onChange={(e) => setDontShowAgain(e.target.checked)}
            />
            <label
              className="form-check-label"
              htmlFor="product-guide-no-again"
            >
              {intl.formatMessage({ id: "app.productGuide.dontShowAgain" })}
            </label>
          </div>
        )}
        <div className="d-flex justify-content-between flex-wrap w-100 align-items-center">
        <Button
          color="link"
          className="text-muted p-0"
          onClick={handleClose}
          type="button"
        >
          {intl.formatMessage({ id: "app.productGuide.skip" })}
        </Button>
        <div className="d-flex align-items-center">
          {safeStep > 0 && (
            <Button
              color="secondary"
              outline
              onClick={goPrev}
              className="mr-2"
              type="button"
            >
              {intl.formatMessage({ id: "app.productGuide.prev" })}
            </Button>
          )}
          <Button color="primary" onClick={goNext} type="button">
            {safeStep < stepCount - 1
              ? intl.formatMessage({ id: "app.productGuide.next" })
              : intl.formatMessage({ id: "app.productGuide.done" })}
          </Button>
        </div>
        </div>
      </ModalFooter>
    </Modal>
  );
}
